import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

export type ToneType =
  | 'Calm'
  | 'Assertive'
  | 'Flirty'
  | 'Boundary'
  | 'Professional'

export interface RewriteResult {
  id?: string
  original_text: string
  rewritten_text: string
  tone: ToneType
  toxicity_score?: number
  created_at?: string
}

export interface RewriteError {
  message: string
  isLimitError?: boolean
}

export function useMessageRewriter() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<RewriteError | null>(null)
  const [history, setHistory] = useState<RewriteResult[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [])

  const fetchHistory = useCallback(async () => {
    if (!user?.id) return

    try {
      setLoadingHistory(true)
      const { data, error: fetchError } = await supabase
        .from('rewrites')
        .select('*')
        .eq('user_id', user.id)
        .not('rewritten_text', 'is', null)
        .order('created_at', { ascending: false })
        .limit(10)

      if (fetchError) {
        console.error('Error fetching rewrite history:', fetchError)
        return
      }

      setHistory(data || [])
    } catch (err) {
      console.error('Failed to fetch history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const rewriteMessage = async (
    text: string,
    tone: ToneType,
  ): Promise<RewriteResult | null> => {
    if (!user?.id) {
      setError({ message: 'You must be logged in to use this feature.' })
      return null
    }

    // Clean up any previous polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Insert a row into the 'rewrites' table
      const { data, error: insertError } = await supabase
        .from('rewrites')
        .insert([
          {
            original_text: text,
            user_id: user.id,
            tone,
          },
        ])
        .select()
        .single()

      // Handle insert errors (RLS policy / credit limit)
      if (insertError) {
        console.error('Insert error:', insertError)

        const errorMsg = (insertError.message || '').toLowerCase()
        const errorCode = insertError.code || ''

        const isLimitOrRLS =
          errorCode === '42501' ||
          errorMsg.includes('policy') ||
          errorMsg.includes('permission') ||
          errorMsg.includes('limit') ||
          errorMsg.includes('credit') ||
          errorMsg.includes('upgrade') ||
          errorMsg.includes('denied') ||
          errorMsg.includes('insufficient')

        if (isLimitOrRLS) {
          setError({
            message:
              "You've used your 3 free daily rewrites. Upgrade for unlimited communication coaching.",
            isLimitError: true,
          })
          setLoading(false)
          return null
        }

        throw new Error(
          insertError.message || 'Failed to submit message for rewriting.',
        )
      }

      if (!data) {
        throw new Error('No data returned from insert.')
      }

      const recordId = data.id

      // 2. Poll for the AI webhook to populate rewritten_text
      const result = await new Promise<RewriteResult>((resolve, reject) => {
        const TIMEOUT_MS = 30000
        const POLL_INTERVAL_MS = 2000
        const startTime = Date.now()

        const checkForResult = async () => {
          try {
            const { data: checkData } = await supabase
              .from('rewrites')
              .select('rewritten_text, toxicity_score, created_at')
              .eq('id', recordId)
              .single()

            if (checkData && checkData.rewritten_text) {
              // Done — clear polling and resolve
              if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
              }
              resolve({
                id: recordId,
                original_text: text,
                rewritten_text: checkData.rewritten_text,
                tone,
                toxicity_score: checkData.toxicity_score || 0,
                created_at: checkData.created_at || new Date().toISOString(),
              })
              return
            }

            // Check timeout
            if (Date.now() - startTime > TIMEOUT_MS) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
              }
              reject(new Error('REWRITE_FAILED'))
            }
          } catch (err) {
            // Network error during poll — don't reject, just wait for next poll
            console.warn('Poll check failed:', err)
          }
        }

        // Check immediately first
        checkForResult()

        // Then poll every 2 seconds
        pollingRef.current = setInterval(checkForResult, POLL_INTERVAL_MS)
      })

      // Add to history immediately
      setHistory((prev) => [result, ...prev].slice(0, 10))

      return result
    } catch (err: any) {
      console.error('Rewrite error:', err)
      setError({
        message:
          err.message === 'REWRITE_FAILED'
            ? 'The AI took too long to respond. Please try again.'
            : 'An unexpected error occurred. Please try again.',
      })
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    rewriteMessage,
    history,
    loading,
    loadingHistory,
    error,
    clearError: () => setError(null),
    refreshHistory: fetchHistory,
  }
}
